// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: MIT-0
import { useState } from "react";

import { downloadFile } from "../util/downloadFile";
import { downloadS3File } from "../util/downloadS3File";
import { translateDocument } from "../util/translateDocument";
import { translatePdf } from "../util/translatePdf";
import { putObjectS3 } from "../../../util/putObjectS3";
import { fetchAuthSession } from "@aws-amplify/auth";
import { v4 as uuid } from "uuid";

const cfnOutputs = require("../../../cfnOutputs.json");

interface TranslationProgress {
	[key: string]: {
		status: "pending" | "translating" | "completed" | "error";
		error?: string;
		usedTerminology?: boolean;
	};
}

export function useDirectTranslation() {
	const [isTranslating, setIsTranslating] = useState<boolean>(false);
	const [progress, setProgress] = useState<TranslationProgress>({});
	const [completedCount, setCompletedCount] = useState<number>(0);
	const [totalCount, setTotalCount] = useState<number>(0);
	const [hasError, setHasError] = useState<boolean>(false);

	const translateDocumentDirectly = async (
		file: File,
		sourceLanguage: string,
		targetLanguages: string[],
	): Promise<boolean> => {
		if (!file || targetLanguages.length === 0) return false;

		const isPdf = file.type === "application/pdf";

		try {
			setIsTranslating(true);
			setCompletedCount(0);
			setTotalCount(targetLanguages.length);
			setHasError(false);

			const initialProgress: TranslationProgress = {};
			targetLanguages.forEach((lang) => {
				initialProgress[lang] = { status: "pending" };
			});
			setProgress(initialProgress);

			if (isPdf) {
				// PDF path: upload to S3, invoke Lambda per language, download DOCX
				const authSession = await fetchAuthSession();
				const identityId = authSession.identityId;
				const jobId = uuid();
				const bucket = cfnOutputs.awsUserFilesS3Bucket;
				const prefix = `private/${identityId}/${jobId}`;
				const inputKey = `${prefix}/upload/${file.name}`;

				// Upload PDF to S3
				await putObjectS3({
					bucketKey: "awsUserFilesS3Bucket",
					path: inputKey,
					file: file,
				});

				// Translate for each target language
				for (const targetLang of targetLanguages) {
					try {
						setProgress((prev) => ({
							...prev,
							[targetLang]: { status: "translating" },
						}));

						const baseName = file.name.replace(/\.pdf$/i, "");
						const outputKey = `${prefix}/output/${baseName}_${targetLang}.docx`;

						await translatePdf({
							bucket,
							inputKey,
							outputKey,
							sourceLanguage,
							targetLanguage: targetLang,
							fileName: file.name,
						});

						// Download the translated DOCX from S3
						await downloadS3File({
							bucketKey: "awsUserFilesS3Bucket",
							path: outputKey,
							fileName: `${baseName}_${targetLang}.docx`,
							contentType:
								"application/vnd.openxmlformats-officedocument.wordprocessingml.document",
						});

						setProgress((prev) => ({
							...prev,
							[targetLang]: { status: "completed" },
						}));
						setCompletedCount((prev) => prev + 1);
					} catch (error) {
						console.error(`Error translating PDF to ${targetLang}:`, error);
						setProgress((prev) => ({
							...prev,
							[targetLang]: {
								status: "error",
								error:
									error instanceof Error ? error.message : "Unknown error",
							},
						}));
						setHasError(true);
					}
				}
			} else {
				// Non-PDF path: direct TranslateDocument API (existing flow)
				for (const targetLang of targetLanguages) {
					try {
						setProgress((prev) => ({
							...prev,
							[targetLang]: { status: "translating" },
						}));

						const result = await translateDocument({
							sourceLanguage,
							targetLanguage: targetLang,
							document: file,
						});

						const fileNameParts = file.name.split(".");
						const extension = fileNameParts.pop() || "";
						const baseName = fileNameParts.join(".");
						const downloadFileName = `${baseName}_${targetLang}.${extension}`;

						downloadFile(
							result.translatedContent,
							downloadFileName,
							file.type,
						);

						setProgress((prev) => ({
							...prev,
							[targetLang]: {
								status: "completed",
								usedTerminology: result.usedTerminology,
							},
						}));
						setCompletedCount((prev) => prev + 1);
					} catch (error) {
						console.error(`Error translating to ${targetLang}:`, error);
						setProgress((prev) => ({
							...prev,
							[targetLang]: {
								status: "error",
								error:
									error instanceof Error ? error.message : "Unknown error",
							},
						}));
						setHasError(true);
					}
				}
			}

			return !hasError;
		} catch (error) {
			console.error("Error in translation process:", error);
			setHasError(true);
			return false;
		} finally {
			setIsTranslating(false);
		}
	};

	return {
		translateDocumentDirectly,
		isTranslating,
		progress,
		completedCount,
		totalCount,
		hasError,
	};
}
